import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmModal from '../../components/ConfirmModal/ConfirmModal';
import CardStats from '../../components/CardStats/CardStats';
import {
  NODE_TYPE,
  createWorkflowDefinition,
  deleteWorkflowDefinition,
  getWorkflowDefinitions,
  updateWorkflowDefinition,
} from '../../services/workflowService';
import { getCurrentUser } from '../../services/authService';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { workflowI18n } from './i18n';
import WorkflowEditorModal from './WorkflowEditorModal';
import './workflowTaskConfig.scss';

const APPROVER_TYPE = {
  USER: 1,
  ROLE: 2,
  GROUP: 3,
};

const parseEnumNumber = (value) => {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string') return null;

  const normalized = value.trim().toLowerCase();
  if (normalized === 'task') return NODE_TYPE.TASK;
  if (normalized === 'approval') return NODE_TYPE.APPROVAL;
  if (normalized === 'user') return APPROVER_TYPE.USER;
  if (normalized === 'role') return APPROVER_TYPE.ROLE;
  if (normalized === 'group') return APPROVER_TYPE.GROUP;

  const asNumber = Number(value);
  return Number.isFinite(asNumber) ? asNumber : null;
};

const resolveApproverLabel = (node, t) => {
  const approverType = parseEnumNumber(node.approverType);

  if (approverType === APPROVER_TYPE.USER) {
    return (
      node.approverUserName ||
      t('wf_approver_user_fallback', { id: node.approverUserId ?? '-' })
    );
  }

  if (approverType === APPROVER_TYPE.ROLE) {
    const rolePrefix = t('wf_approver_role_prefix');
    const roleName =
      node.approverRoleName ||
      t('wf_approver_role_fallback', { id: node.approverRoleId ?? '-' });
    return roleName.startsWith(rolePrefix)
      ? roleName
      : `${rolePrefix} ${roleName}`;
  }

  if (approverType === APPROVER_TYPE.GROUP) {
    const groupPrefix = t('wf_approver_group_prefix');
    const groupName =
      node.approverGroupName ||
      t('wf_approver_group_fallback', { id: node.approverGroupId ?? '-' });
    return groupName.startsWith(groupPrefix)
      ? groupName
      : `${groupPrefix} ${groupName}`;
  }

  return t('wf_approver_undefined');
};

const mapDefinitionToRow = (definition, t) => {
  const nodes = Array.isArray(definition.nodes) ? definition.nodes : [];
  const approvalNodes = nodes.filter((node) => {
    const nodeType = parseEnumNumber(node.nodeType);
    return nodeType === NODE_TYPE.APPROVAL;
  });

  const approvers = [
    ...new Set(
      approvalNodes.map((node) => resolveApproverLabel(node, t)).filter(Boolean)
    ),
  ];

  return {
    id: definition.id,
    task: definition.name,
    code: definition.nomenclature,
    approvers,
    approvalsCount: approvalNodes.length,
    status: definition.isActive ? 'active' : 'inactive',
  };
};

const renderApprovers = (approvers, Badge) => {
  const maxVisible = 2;
  const visible = approvers.slice(0, maxVisible);
  const hiddenCount = Math.max(0, approvers.length - maxVisible);

  return (
    <div className='workflow-approver-list'>
      {visible.map((approver) => (
        <Badge key={approver} text={approver} color='info' size='sm' />
      ))}
      {hiddenCount > 0 && (
        <Badge text={`+${hiddenCount}`} color='light' size='sm' />
      )}
    </div>
  );
};

const buildLocalUpdatedDefinition = (
  definitionId,
  payload,
  previousDefinition
) => {
  const previousNodes = Array.isArray(previousDefinition?.nodes)
    ? previousDefinition.nodes
    : [];
  const previousTransitions = Array.isArray(previousDefinition?.transitions)
    ? previousDefinition.transitions
    : [];

  const nodes = (payload?.nodes || []).map((node, index) => {
    const previousNode = previousNodes[index];
    return {
      id: previousNode?.id || 100000 + index,
      code: node.code,
      name: node.name,
      nodeType: node.nodeType,
      sequenceOrder: node.sequenceOrder,
      isRequired: node.isRequired,
      approverType: node.approverType,
      approverUserId: node.approverUserId,
      approverRoleId: node.approverRoleId,
      approverGroupId: node.approverGroupId,
      approverUserName: node.approverUserId
        ? node.approverUserName || null
        : null,
      approverRoleName: node.approverRoleId
        ? node.approverRoleName || null
        : null,
      approverGroupName: node.approverGroupId
        ? node.approverGroupName || null
        : null,
    };
  });

  const nodeIdByKey = nodes.reduce((acc, node, index) => {
    const key = payload.nodes[index]?.key;
    if (key) acc[key] = node.id;
    return acc;
  }, {});

  const transitions = (payload?.transitions || []).map((transition, index) => {
    const previousTransition = previousTransitions[index];

    return {
      id: previousTransition?.id || 100000 + index,
      fromWorkflowNodeId:
        transition.fromNodeKey === null
          ? null
          : nodeIdByKey[transition.fromNodeKey] || null,
      toWorkflowNodeId: nodeIdByKey[transition.toNodeKey] || null,
      transitionOrder: transition.transitionOrder,
      isRequired: transition.isRequired,
    };
  });

  return {
    id: definitionId,
    name: payload.name,
    nomenclature: payload.nomenclature,
    description: payload.description,
    isActive: payload.isActive,
    nodes,
    transitions,
  };
};

const upsertDefinition = (definitions, nextDefinition) => {
  if (!nextDefinition?.id) return definitions;
  const exists = definitions.some((item) => item.id === nextDefinition.id);
  if (exists) {
    return definitions.map((item) =>
      item.id === nextDefinition.id ? nextDefinition : item
    );
  }
  return [nextDefinition, ...definitions];
};

const WorkflowTaskConfig = ({ DesignSystemComponents }) => {
  const { Card, Table, Button, IconButton, SvgIcon, Modal, Badge } =
    DesignSystemComponents;
  const currentUserId = getCurrentUser()?.userId || null;

  const { language } = useLanguage();
  const wfLanguage = normalizeSupportedLanguage(language);
  const { t } = useTranslation(undefined, { i18n: workflowI18n });

  useEffect(() => {
    workflowI18n.changeLanguage(wfLanguage);
  }, [wfLanguage]);

  const [workflowDefinitions, setWorkflowDefinitions] = useState([]);
  const [workflowRows, setWorkflowRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [savingDefinition, setSavingDefinition] = useState(false);
  const [deletingDefinitionId, setDeletingDefinitionId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [editorModalState, setEditorModalState] = useState({
    show: false,
    mode: 'create',
    definition: null,
  });

  const loadDefinitions = async ({ preserveDataOnError = false } = {}) => {
    setLoading(true);
    setError('');

    try {
      const definitions = await getWorkflowDefinitions();
      setWorkflowDefinitions(definitions);
      setWorkflowRows(definitions.map((def) => mapDefinitionToRow(def, t)));
      return definitions;
    } catch (serviceError) {
      setError(
        serviceError?.customMessage ||
          serviceError?.message ||
          t('wf_error_load')
      );
      if (!preserveDataOnError) {
        setWorkflowDefinitions([]);
        setWorkflowRows([]);
        throw serviceError;
      }
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadDefinitions().catch(() => null);
  }, []);

  const closeEditorModal = () => {
    if (savingDefinition) return;

    setEditorModalState({
      show: false,
      mode: 'create',
      definition: null,
    });
  };

  const openCreateModal = () => {
    setEditorModalState({
      show: true,
      mode: 'create',
      definition: null,
    });
  };

  const openEditModal = (definitionId) => {
    const definition = workflowDefinitions.find(
      (item) => item.id === definitionId
    );
    if (!definition) return;

    setEditorModalState({
      show: true,
      mode: 'edit',
      definition,
    });
  };

  const handleEditorSave = async ({ mode, definitionId, payload }) => {
    setSavingDefinition(true);
    setError('');

    try {
      if (mode === 'edit' && definitionId) {
        const updatedResponse = await updateWorkflowDefinition(
          definitionId,
          payload
        );
        const updatedDefinition = updatedResponse?.id
          ? updatedResponse
          : buildLocalUpdatedDefinition(
              definitionId,
              payload,
              editorModalState.definition
            );

        setWorkflowDefinitions((prev) =>
          upsertDefinition(prev, updatedDefinition)
        );
        setWorkflowRows((prev) =>
          upsertDefinition(prev, mapDefinitionToRow(updatedDefinition, t))
        );
      } else {
        const createdResponse = await createWorkflowDefinition(payload);
        const createdId =
          Number(createdResponse?.id) > 0
            ? Number(createdResponse.id)
            : Number(`9${Date.now()}`);
        const createdDefinition = createdResponse?.id
          ? createdResponse
          : buildLocalUpdatedDefinition(createdId, payload, null);

        setWorkflowDefinitions((prev) =>
          upsertDefinition(prev, createdDefinition)
        );
        setWorkflowRows((prev) =>
          upsertDefinition(prev, mapDefinitionToRow(createdDefinition, t))
        );
      }

      closeEditorModal();
      await loadDefinitions({ preserveDataOnError: true });
    } catch (serviceError) {
      setError(
        serviceError?.customMessage ||
          serviceError?.message ||
          t('wf_error_save')
      );
    } finally {
      setSavingDefinition(false);
    }
  };

  const handleDeleteDefinition = async () => {
    if (!deleteTarget?.id || deletingDefinitionId) return;

    setDeletingDefinitionId(deleteTarget.id);
    setError('');

    try {
      await deleteWorkflowDefinition(deleteTarget.id);
      await loadDefinitions();
      setDeleteTarget(null);
    } catch (serviceError) {
      setError(
        serviceError?.customMessage ||
          serviceError?.message ||
          t('wf_error_delete')
      );
    } finally {
      setDeletingDefinitionId(null);
    }
  };

  const stats = useMemo(() => {
    const totalWorkflows = workflowRows.length;
    const activeWorkflows = workflowRows.filter(
      (item) => item.status === 'active'
    ).length;
    const totalApprovals = workflowRows.reduce(
      (sum, item) => sum + item.approvalsCount,
      0
    );

    return { totalWorkflows, activeWorkflows, totalApprovals };
  }, [workflowRows]);

  return (
    <div className='workflow-task-config-page'>
      <div className='d-flex justify-content-between align-items-start mb-4 gap-3'>
        <div>
          <h2 className='fw-semibold mb-1'>{t('wf_page_title')}</h2>
          <h4 className='fw-normal'>{t('wf_page_subtitle')}</h4>
        </div>
        <Button
          label={t('wf_btn_new')}
          iconStart='plusLg'
          variant='primary'
          size='sm'
          onClick={openCreateModal}
          disabled={savingDefinition}
        />
      </div>

      {error && <p className='color-error-500 mb-3'>{error}</p>}

      <div className='row g-3 mb-4'>
        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('wf_kpi_total')}
            count={stats.totalWorkflows}
            icon='listTask'
            subtitle=''
          />
        </div>

        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('wf_kpi_active')}
            count={stats.activeWorkflows}
            icon='checkCircle'
            type='success'
            subtitle=''
          />
        </div>

        <div className='col-12 col-md-4'>
          <CardStats
            DesignSystemComponents={DesignSystemComponents}
            title={t('wf_kpi_approvals')}
            count={stats.totalApprovals}
            subtitle=''
          />
        </div>
      </div>

      <Card specialPadding='p-3'>
        <div className='px-2 pt-1 pb-3'>
          <h3 className='fw-semibold mb-0'>{t('wf_table_title')}</h3>
        </div>
        <div className='table-container table-workflow-config'>
          <Table
            columns={[
              {
                accessor: 'task',
                label: t('wf_col_task'),
                render: (row) => (
                  <div className='d-flex align-items-center gap-2'>
                    <SvgIcon
                      name='listTask'
                      size='14px'
                      stroke='var(--primary-500)'
                    />
                    <span className='caption'>{row.task}</span>
                  </div>
                ),
              },
              {
                accessor: 'code',
                label: t('wf_col_code'),
                render: (row) => (
                  <Badge text={row.code} color='light' size='sm' />
                ),
              },
              {
                accessor: 'approvers',
                label: t('wf_col_approvers'),
                render: (row) => renderApprovers(row.approvers, Badge),
              },
              {
                accessor: 'approvalsCount',
                label: t('wf_col_steps'),
                render: (row) => (
                  <Badge
                    text={`${row.approvalsCount} ${
                      row.approvalsCount === 1
                        ? t('wf_step_singular')
                        : t('wf_step_plural')
                    }`}
                    color='warning'
                    size='sm'
                  />
                ),
              },
              {
                accessor: 'status',
                label: t('wf_col_status'),
                render: (row) => (
                  <Badge
                    text={
                      row.status === 'active'
                        ? t('wf_status_active')
                        : t('wf_status_inactive')
                    }
                    color={row.status === 'active' ? 'success' : 'light'}
                    size='sm'
                  />
                ),
              },
              {
                label: t('wf_col_actions'),
                render: (row) => (
                  <div className='d-flex align-items-center gap-1'>
                    <IconButton
                      ariaLabel={t('wf_aria_edit')}
                      icon='eye'
                      size='md'
                      onClick={() => openEditModal(row.id)}
                      iconStroke='var(--primary-500)'
                      disabled={
                        savingDefinition || deletingDefinitionId === row.id
                      }
                    />
                    <IconButton
                      ariaLabel={t('wf_aria_delete')}
                      icon='trash'
                      size='md'
                      onClick={() =>
                        setDeleteTarget({ id: row.id, task: row.task })
                      }
                      iconStroke='var(--error-500)'
                      disabled={
                        savingDefinition || deletingDefinitionId === row.id
                      }
                    />
                  </div>
                ),
              },
            ]}
            data={workflowRows}
            loading={loading}
          />
        </div>
      </Card>

      <WorkflowEditorModal
        DesignSystemComponents={DesignSystemComponents}
        show={editorModalState.show}
        mode={editorModalState.mode}
        initialDefinition={editorModalState.definition}
        existingDefinitions={workflowDefinitions}
        currentUserId={currentUserId}
        onClose={closeEditorModal}
        onSaved={handleEditorSave}
      />

      <ConfirmModal
        Modal={Modal}
        show={!!deleteTarget}
        title={t('wf_delete_title')}
        message={
          deleteTarget
            ? t('wf_delete_message', { name: deleteTarget.task })
            : t('wf_delete_message_fallback')
        }
        confirmLabel={t('wf_delete_confirm')}
        confirmVariant='danger'
        onConfirm={handleDeleteDefinition}
        onClose={() => {
          if (deletingDefinitionId) return;
          setDeleteTarget(null);
        }}
      />
    </div>
  );
};

export default WorkflowTaskConfig;
